/**
 * webBlockRegistry.tsx — Mapa bloco -> componente React (saída web)
 *
 * O catálogo de blocos (Studio Core) diz o que existe; este registro diz como
 * cada bloco vira interface no navegador. Um bloco sem componente registrado é
 * simplesmente ignorado, nunca quebra a página.
 *
 * RELEASE: v1.5.0 - 2026-09-11 - Seção em HTML livre (section.html)
 * RELEASE: v1.4.0 - 2026-09-11 - Bloco "Forma decorativa" (ui.shape) — Fase 6
 * RELEASE: v1.3.0 - 2026-09-11 - Colunas usam a grade interna de 12 colunas (Fase 2)
 * RELEASE: v1.2.0 - 2026-09-10 - Primitivos e seção com camadas (motor de modelos)
 * RELEASE: v1.1.0 - 2026-09-10 - Blocos de página pública (landing)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 2)
 */

import { resolveAssetUrl } from '@/lib/studio/core/assets/stockCatalog';
import { classesDaColuna } from '@/lib/studio/core/layout/grid';
import type { StudioResponsiveValue } from '@/lib/studio/core/types';
import '@/lib/studio/render/web/studioLayers.css';
import React from 'react';
import type { StudioWebBlockComponent } from './types';
// Blocos interativos oficiais: desenho vindo do módulo único do compilador.
import { STUDIO_INTERACTIVE_WEB_BLOCKS } from './interactiveBlocks';
import {
  PageFeaturesBlock,
  PageFormBlock,
  PageHeroBlock,
  PageTestimonialsBlock,
} from './pageBlocks';
import {
  SectionBannerBlock,
  SectionContactBlock,
  SectionCtaBlock,
  SectionDeadlineBlock,
  SectionFaqBlock,
  SectionFooterBlock,
  SectionGalleryBlock,
  SectionHeadingBlock,
  SectionLogosBlock,
  SectionNavBlock,
  RawHtmlSectionBlock,
  SectionPricingBlock,
  SectionSplitBlock,
  SectionStatsBlock,
  SectionStepsBlock,
  SectionTeamBlock,
  SectionVideoBlock,
} from './sectionBlocks';

import {
  ProposalAcceptBlock,
  ProposalConditionsBlock,
  ProposalDecisionBlock,
  ProposalFooterBlock,
  ProposalHeroBlock,
  ProposalItemsBlock,
  ProposalRejectBlock,
  ProposalStatusBlock,
  ProposalTotalsBlock,
} from './proposalBlocks';

import {
  AvatarsBlock,
  BadgeBlock,
  ButtonsBlock,
  CardBlock,
  DeviceBlock,
  ImagePrimitiveBlock,
  LayeredSectionBlock,
  LogosBlock,
  ParagraphBlock,
  ShapeBlock,
  StackBlock,
  StatBlock,

  TitleBlock,
  VideoPrimitiveBlock,
} from './primitiveBlocks';

const registry = new Map<string, StudioWebBlockComponent>();

/** Registra (ou substitui) o componente web de um bloco. */
export function registerWebBlock(key: string, component: StudioWebBlockComponent): void {
  registry.set(key, component);
}

/** Retorna o componente web de um bloco. */
export function getWebBlock(key: string): StudioWebBlockComponent | undefined {
  return registry.get(key);
}

// =============================================================================
// Blocos base de layout e conteúdo
// =============================================================================

const SectionBlock: StudioWebBlockComponent = ({ node, children }) => (
  <section
    className="proposal-surface proposal-reveal"
    style={{
      backgroundColor: (node.props.backgroundColor as string) || undefined,
      padding: (node.props.padding as string) || '24px',
      maxWidth: (node.props.maxWidth as string) || undefined,
    }}
  >
    {children}
  </section>
);

/**
 * Colunas sobre a grade interna de 12 colunas (Fase 2).
 * Cada filho recebe a largura da proporção escolhida, com ajuste opcional para
 * tablet e celular. Sem proporção, o comportamento antigo é mantido.
 */
const ColumnsBlock: StudioWebBlockComponent = ({ node, children }) => {
  const filhos = React.Children.toArray(children);
  const preset = node.props.preset as StudioResponsiveValue<string> | undefined;
  return (
    <div
      className="st-grid"
      style={{
        gap: (node.props.gap as string) || '16px',
        alignItems: (node.props.alignItems as React.CSSProperties['alignItems']) || undefined,
      }}
    >
      {filhos.map((filho, indice) => (
        <div
          key={indice}
          className={classesDaColuna(preset, indice, filhos.length, node.props.count)}
        >
          {filho}
        </div>
      ))}
    </div>
  );
};

const SpacerBlock: StudioWebBlockComponent = ({ node }) => (
  <div style={{ height: (node.props.height as string) || '24px' }} />
);

const DividerBlock: StudioWebBlockComponent = ({ node }) => (
  <hr
    style={{
      borderTopWidth: (node.props.thickness as string) || '1px',
      borderColor: (node.props.color as string) || undefined,
    }}
  />
);

const TextBlock: StudioWebBlockComponent = ({ node }) => (
  <div
    className="prose prose-sm max-w-none text-sm"
    style={{
      textAlign: (node.props.align as React.CSSProperties['textAlign']) || 'left',
      color: (node.props.color as string) || undefined,
    }}
    // Conteúdo sanitizado no momento em que o modelo é salvo/publicado
    dangerouslySetInnerHTML={{ __html: (node.props.html as string) || '' }}
  />
);

const ImageBlock: StudioWebBlockComponent = ({ node }) => {
  const align = (node.props.align as string) || 'center';
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
  return (
    <div style={{ display: 'flex', justifyContent: justify }}>
      <img
        src={resolveAssetUrl((node.props.src as string) || '')}
        alt={(node.props.alt as string) || ''}
        style={{ width: (node.props.width as string) || '100%' }}
        loading="lazy"
      />
    </div>
  );
};

const ButtonBlock: StudioWebBlockComponent = ({ node }) => (
  <a
    href={(node.props.href as string) || '#'}
    className="inline-flex h-11 items-center rounded-xl px-6 text-sm font-semibold"
    style={{
      backgroundColor: (node.props.backgroundColor as string) || undefined,
      color: (node.props.textColor as string) || undefined,
    }}
    target="_blank"
    rel="noopener noreferrer"
  >
    {(node.props.label as string) || 'Clique aqui'}
  </a>
);

const TableBlock: StudioWebBlockComponent = ({ node }) => (
  <div
    className="prose prose-sm max-w-none text-sm"
    dangerouslySetInnerHTML={{ __html: (node.props.html as string) || '' }}
  />
);

registerWebBlock('layout.section', SectionBlock);
registerWebBlock('layout.columns', ColumnsBlock);
registerWebBlock('layout.spacer', SpacerBlock);
registerWebBlock('layout.divider', DividerBlock);
registerWebBlock('common.text', TextBlock);
registerWebBlock('common.image', ImageBlock);
registerWebBlock('common.button', ButtonBlock);
registerWebBlock('common.table', TableBlock);

// =============================================================================
// Blocos de proposta e de ação
// =============================================================================

registerWebBlock('proposal.hero', ProposalHeroBlock);
registerWebBlock('proposal.status', ProposalStatusBlock);
registerWebBlock('proposal.items', ProposalItemsBlock);
registerWebBlock('proposal.totals', ProposalTotalsBlock);
registerWebBlock('proposal.conditions', ProposalConditionsBlock);
registerWebBlock('proposal.footer', ProposalFooterBlock);
registerWebBlock('action.proposal.decision', ProposalDecisionBlock);
registerWebBlock('action.proposal.accept', ProposalAcceptBlock);
registerWebBlock('action.proposal.reject', ProposalRejectBlock);

// =============================================================================
// Blocos de página pública (landing)
// =============================================================================

registerWebBlock('page.hero', PageHeroBlock);
registerWebBlock('page.features', PageFeaturesBlock);
registerWebBlock('page.testimonials', PageTestimonialsBlock);
registerWebBlock('page.form', PageFormBlock);

// =============================================================================
// Blocos de seção genéricos (construtor de blocos prontos)
// =============================================================================

registerWebBlock('page.heading', SectionHeadingBlock);
registerWebBlock('page.split', SectionSplitBlock);
registerWebBlock('page.gallery', SectionGalleryBlock);
registerWebBlock('page.video', SectionVideoBlock);
registerWebBlock('page.stats', SectionStatsBlock);
registerWebBlock('page.logos', SectionLogosBlock);
registerWebBlock('page.pricing', SectionPricingBlock);
registerWebBlock('page.faq', SectionFaqBlock);
registerWebBlock('page.steps', SectionStepsBlock);
registerWebBlock('page.team', SectionTeamBlock);
registerWebBlock('page.cta', SectionCtaBlock);
registerWebBlock('page.deadline', SectionDeadlineBlock);
registerWebBlock('page.contact', SectionContactBlock);
registerWebBlock('page.nav', SectionNavBlock);
registerWebBlock('page.footer', SectionFooterBlock);
registerWebBlock('page.banner', SectionBannerBlock);
// Seção em HTML livre (página criada por IA e importada como blocos)
registerWebBlock('section.html', RawHtmlSectionBlock);

// =============================================================================
// Primitivos e seção com camadas (motor de modelos)
// =============================================================================

registerWebBlock('section.layered', LayeredSectionBlock);
registerWebBlock('layout.stack', StackBlock);
registerWebBlock('ui.badge', BadgeBlock);
registerWebBlock('ui.title', TitleBlock);
registerWebBlock('ui.paragraph', ParagraphBlock);
registerWebBlock('ui.buttons', ButtonsBlock);
registerWebBlock('ui.image', ImagePrimitiveBlock);
registerWebBlock('ui.card', CardBlock);
registerWebBlock('ui.avatars', AvatarsBlock);
registerWebBlock('ui.stat', StatBlock);
registerWebBlock('ui.logos', LogosBlock);
registerWebBlock('ui.device', DeviceBlock);
registerWebBlock('ui.video', VideoPrimitiveBlock);
registerWebBlock('ui.shape', ShapeBlock);

// Blocos interativos oficiais (biblioteca própria, Fases 3 e 4).
STUDIO_INTERACTIVE_WEB_BLOCKS.forEach(([chave, componente]) => registerWebBlock(chave, componente));
